
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import SpecialtySelect from "@/components/psychologist/SpecialtySelect";
import LanguageSelect from "@/components/psychologist/LanguageSelect";

interface ProfessionalInfoFormSectionProps {
  formData: any;
  editing: boolean;
  onInputChange: (field: string, value: any) => void;
  formatCRP: (value: string) => string;
}

const ProfessionalInfoFormSection: React.FC<ProfessionalInfoFormSectionProps> = ({
  formData,
  editing,
  onInputChange,
  formatCRP
}) => {
  return (
    <Card className="md:col-span-2">
      <CardHeader>
        <CardTitle>Informações Profissionais Completas</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* CRP */}
        <div className="space-y-2">
          <Label htmlFor="crp">CRP *</Label>
          <Input 
            id="crp"
            value={formData.crp} 
            onChange={(e) => onInputChange('crp', formatCRP(e.target.value))}
            disabled={!editing}
            className={!editing ? "bg-muted" : ""}
            placeholder="XX/XXXXXX"
            maxLength={9}
          />
          <p className="text-xs text-muted-foreground">Formato: XX/XXXXXX</p>
        </div>

        {/* Specialties */}
        <div className="space-y-2">
          <Label>Especialidades * (máx. 5)</Label>
          <SpecialtySelect
            value={formData.specialties}
            onChange={(value) => onInputChange('specialties', value)}
          />
        </div>

        {/* Languages */}
        <div className="space-y-2">
          <Label>Idiomas (máx. 6)</Label>
          <LanguageSelect
            selectedLanguages={formData.languages}
            onLanguagesChange={(value) => onInputChange('languages', value)}
            maxLanguages={6}
          />
        </div>

        {/* Biography */}
        <div className="space-y-2">
          <Label htmlFor="bio">Biografia Completa</Label>
          <Textarea 
            id="bio"
            value={formData.bio} 
            onChange={(e) => onInputChange('bio', e.target.value)}
            disabled={!editing}
            className={!editing ? "bg-muted" : ""}
            rows={4}
            placeholder="Conte sobre sua trajetória profissional, experiência e abordagem terapêutica..."
          />
        </div>

        {/* Short Bio */}
        <div className="space-y-2">
          <Label htmlFor="short_bio">Resumo Profissional</Label>
          <Textarea 
            id="short_bio"
            value={formData.short_bio} 
            onChange={(e) => onInputChange('short_bio', e.target.value)}
            disabled={!editing}
            className={!editing ? "bg-muted" : ""}
            rows={2}
            placeholder="Resumo conciso para exibição no perfil público..."
          />
        </div>

        {/* Education */}
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="university">Universidade</Label>
            <Input 
              id="university"
              value={formData.university} 
              onChange={(e) => onInputChange('university', e.target.value)}
              disabled={!editing}
              className={!editing ? "bg-muted" : ""}
              placeholder="Nome da universidade"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="graduation_year">Ano de Formação</Label>
            <Input 
              id="graduation_year"
              type="number"
              value={formData.graduation_year} 
              onChange={(e) => onInputChange('graduation_year', e.target.value)}
              disabled={!editing}
              className={!editing ? "bg-muted" : ""}
              placeholder="2020"
              min="1950"
              max={new Date().getFullYear()}
            />
          </div>
        </div>

        {/* Academic Background */}
        <div className="space-y-2">
          <Label htmlFor="education">Formação Acadêmica</Label>
          <Textarea 
            id="education"
            value={formData.education} 
            onChange={(e) => onInputChange('education', e.target.value)}
            disabled={!editing}
            className={!editing ? "bg-muted" : ""}
            rows={3}
            placeholder="Detalhes sobre sua formação acadêmica, pós-graduação, mestrado, doutorado..."
          />
        </div>

        {/* Professional Experience */}
        <div className="space-y-2">
          <Label htmlFor="experience">Experiência Profissional</Label>
          <Textarea 
            id="experience"
            value={formData.experience} 
            onChange={(e) => onInputChange('experience', e.target.value)}
            disabled={!editing}
            className={!editing ? "bg-muted" : ""}
            rows={4}
            placeholder="Histórico profissional, locais de trabalho, experiências relevantes..."
          />
        </div>

        {/* Therapeutic Approach */}
        <div className="space-y-2">
          <Label htmlFor="approach">Abordagem Terapêutica</Label>
          <Textarea 
            id="approach"
            value={formData.approach} 
            onChange={(e) => onInputChange('approach', e.target.value)}
            disabled={!editing}
            className={!editing ? "bg-muted" : ""}
            rows={3}
            placeholder="Metodologias e abordagens terapêuticas que você utiliza..."
          />
        </div>

        {/* Certifications */}
        <div className="space-y-2">
          <Label htmlFor="certifications">Certificações e Cursos</Label>
          <Textarea 
            id="certifications"
            value={formData.certifications} 
            onChange={(e) => onInputChange('certifications', e.target.value)}
            disabled={!editing}
            className={!editing ? "bg-muted" : ""}
            rows={3}
            placeholder="Cursos, certificações, workshops e especializações..."
          />
        </div>

        {/* Personal Statement */}
        <div className="space-y-2">
          <Label htmlFor="personal_statement">Declaração Pessoal</Label>
          <Textarea 
            id="personal_statement"
            value={formData.personal_statement} 
            onChange={(e) => onInputChange('personal_statement', e.target.value)}
            disabled={!editing}
            className={!editing ? "bg-muted" : ""}
            rows={3}
            placeholder="Mensagem pessoal para seus pacientes..."
          />
        </div>
      </CardContent>
    </Card>
  );
};

export default ProfessionalInfoFormSection;
